import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-amber text-amber-ink hover:bg-amber/90",
        secondary: "bg-bg-3 text-chalk hover:bg-line",
        outline: "border border-line bg-transparent text-chalk hover:bg-bg-3",
        ghost: "text-chalk hover:bg-bg-3",
        destructive: "bg-bad text-white hover:bg-bad/90",
        ok: "bg-ok text-ok-ink hover:bg-ok/90",
      },
      size: {
        default: "h-11 px-5 text-16",
        sm: "h-9 px-3 text-14",
        lg: "h-14 px-8 text-22",
        xl: "h-16 px-10 text-28 rounded-lg",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
